import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Loader2, FileText, Sparkles, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { ProfileHeader } from '../components/ProfileHeader';
import { WallPostCard } from '../components/WallPostCard';
import { BadgeCard } from '../components/BadgeCard';
import { ReactionSummary } from '../components/ReactionSummary';
import { useUserProfile, useProfileStats, useIsFollowing } from '../hooks/useUserProfile';
import { useUserPosts } from '../hooks/useUserPosts';
import { useUserBadges } from '../hooks/useUserBadges';
import { useUserReactionStats } from '../hooks/useUserReactionStats';
import { usePinnedPost } from '../hooks/usePinnedPost';

type ProfileTab = 'posts' | 'reactions' | 'badges';

const TABS: { value: ProfileTab; label: string; icon: React.ElementType }[] = [
  { value: 'posts', label: 'Posts', icon: FileText },
  { value: 'reactions', label: 'Reactions', icon: Sparkles },
  { value: 'badges', label: 'Badges', icon: Award },
];

export function ProfilePage() {
  const { username } = useParams<{ username?: string }>();
  const { user, isLoading: authLoading, isAuthenticated } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<ProfileTab>('posts');

  // If viewing /profile with no username, must be authenticated
  const isViewingOwnProfile = !username;
  
  // Profile data - pass username or undefined for current user
  const { profile, isLoading: loadingProfile, isCurrentUser } = useUserProfile(username);
  const { data: stats, isLoading: loadingStats } = useProfileStats(profile?.id);
  const { data: isFollowing } = useIsFollowing(profile?.id);

  // Posts data
  const {
    data: postsData,
    isLoading: loadingPosts,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useUserPosts(profile?.id);
  const posts = postsData?.pages.flatMap(page => page.posts) || [];
  const nonPinnedPosts = posts.filter(p => !p.is_pinned);

  // Pinned post
  const { data: pinnedPost, invalidate: invalidatePinned } = usePinnedPost(profile?.id);

  // Badges data
  const { data: badges, isLoading: loadingBadges } = useUserBadges(profile?.id);

  // Reactions data
  const { data: reactionStats, isLoading: loadingReactions } = useUserReactionStats(profile?.id);

  // Infinite scroll for posts
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && activeTab === 'posts') {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, activeTab]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  // If viewing own profile without auth, redirect to login
  if (isViewingOwnProfile && !authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (authLoading || loadingProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-foreground font-medium">Profile not found</p>
        <p className="text-muted-foreground text-sm mt-1">
          This user may not exist or the profile is private.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Profile Header */}
      <ProfileHeader
        profile={profile}
        stats={stats}
        isCurrentUser={isCurrentUser}
        isFollowing={isFollowing}
      />

      {/* Tab Navigation */}
      <div className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  "flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors relative",
                  activeTab === tab.value
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {activeTab === tab.value && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'posts' && (
          <>
            {loadingPosts ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : posts.length === 0 && !pinnedPost ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <span className="text-4xl mb-3">📝</span>
                <p className="text-foreground font-medium text-center">
                  {isCurrentUser ? "You haven't posted yet" : "No posts yet"}
                </p>
                <p className="text-muted-foreground text-sm text-center mt-1">
                  {isCurrentUser ? "Share your first post!" : "Check back later for updates."}
                </p>
              </div>
            ) : (
              <>
                {/* Pinned Post */}
                {pinnedPost && (
                  <div className="p-2">
                    <WallPostCard 
                      post={pinnedPost} 
                      showPinBadge 
                      onPinChange={invalidatePinned}
                    />
                  </div>
                )}
                
                {/* Regular Posts */}
                {nonPinnedPosts.map((post) => (
                  <WallPostCard key={post.id} post={post} onPinChange={invalidatePinned} />
                ))}
                <div ref={loadMoreRef} className="py-4">
                  {isFetchingNextPage && (
                    <div className="flex justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  )}
                  {!hasNextPage && posts.length > 0 && (
                    <p className="text-center text-muted-foreground text-sm">
                      No more posts
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'reactions' && (
          <>
            {loadingReactions ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <ReactionSummary stats={reactionStats || []} />
            )}
          </>
        )}

        {activeTab === 'badges' && (
          <>
            {loadingBadges ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : !badges || badges.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <span className="text-4xl mb-3">🏆</span>
                <p className="text-foreground font-medium text-center">No badges yet</p>
                <p className="text-muted-foreground text-sm text-center mt-1">
                  Keep posting and engaging to earn badges!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 p-4">
                {badges.map((badge) => (
                  <BadgeCard key={badge.id} badge={badge} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
